/**
 * Tarifas — governed pricing rules view-model (W1-ADMIN, D358; the screen deferred at D330). A pricing rule is a fare
 * rate table for one market + service category, registered as a new version (the server creates and submits it to
 * PENDING_APPROVAL in one step) and made ACTIVE only when a **second** operator approves it (four-eyes, refused by the
 * server when the approver is the maker). Everything derivable is pure and tested here; the screen only renders it.
 *
 * Human edges, never cents: amounts arrive from `MoneyInput` as integer minor units (bigint) and leave as the wire's
 * decimal strings; the effective window is typed in the market's wall clock and leaves as UTC instants; distance rates
 * are charged per meters and time rates per seconds (SI storage), rendered by the screen with `@vello/format`.
 */
import { type Result, ok, err, isIso4217Currency } from '@vello/core';
import { type CreatePricingRuleInput, type PricingDistanceBandOnWire, type PricingIntercityPairOnWire, type PricingRuleStatus, type PricingRuleView } from '@vello/api-client';
import { zonedLocalToUtc } from './market-time.js';

/** D463 (B-040): one distance band as typed — kilometres (up to 3 decimals) and the band's fixed price. */
export interface TariffBandRow {
  readonly fromKm: string;
  readonly toKm: string;
  readonly priceMinor: bigint | undefined;
}

/** D463 (B-041): one intercity pair as typed — the destination market and its fixed and/or additional price. */
export interface TariffPairRow {
  readonly destinationMarketId: string;
  readonly fixedMinor: bigint | undefined;
  readonly additionalMinor: bigint | undefined;
}

export interface TariffForm {
  readonly marketId: string;
  readonly categoryCode: string;
  readonly currency: string;
  /** `datetime-local` value in the market's wall clock. */
  readonly effectiveFromLocal: string;
  /** Optional `datetime-local` end, in the market's wall clock. */
  readonly effectiveToLocal: string;
  readonly baseFareMinor: bigint | undefined;
  readonly minimumFareMinor: bigint | undefined;
  readonly distanceRateMinor: bigint | undefined;
  /** The distance the rate is charged over, in meters (e.g. 1000). */
  readonly distancePerMeters: string;
  readonly timeRateMinor: bigint | undefined;
  /** The time the rate is charged over, in seconds (e.g. 60). */
  readonly timePerSeconds: string;
  readonly quoteTtlSeconds: string;
  /** D463 (C-03): the fare components — all optional. */
  readonly perStopFeeMinor: bigint | undefined;
  /** The flat platform fee ("taxa fixa"). */
  readonly bookingFeeMinor: bigint | undefined;
  /** The platform fee over the fare, in percent with up to 2 decimals ("taxa percentual"). */
  readonly percentFeePercent: string;
  /** The wait charge per MINUTE held. */
  readonly waitRateMinor: bigint | undefined;
  readonly freeWaitMinutes: string;
  readonly waitPenaltyMinor: bigint | undefined;
  /** The most minutes held a ride is charged for (required with a wait charge). */
  readonly maxWaitMinutes: string;
  readonly bands: readonly TariffBandRow[];
  readonly pairs: readonly TariffPairRow[];
  readonly reason: string;
}

export type TariffFormField = keyof TariffForm;

export type TariffFormIssueCode =
  | 'REQUIRED'
  | 'INVALID_MARKET'
  | 'INVALID_CATEGORY'
  | 'INVALID_CURRENCY'
  | 'INVALID_LOCAL_TIME'
  | 'WINDOW_ORDER'
  | 'INVALID_POSITIVE_INTEGER'
  | 'INVALID_PERCENT'
  | 'INVALID_MINUTES'
  | 'WAIT_INCOMPLETE'
  | 'INVALID_BANDS'
  | 'INVALID_PAIRS';

export interface TariffFormIssue {
  readonly field: TariffFormField;
  readonly code: TariffFormIssueCode;
}

const MARKET = /^[a-z]{2}:[a-z0-9]+(?:-[a-z0-9]+)*$/;
const CATEGORY = /^[a-z0-9][a-z0-9_-]{0,31}$/;
const POSITIVE_INT = /^[1-9]\d{0,8}$/;
const PERCENT = /^\d{1,2}(?:\.\d{1,2})?$/;
const KM = /^\d{1,4}(?:\.\d{1,3})?$/;
const NON_NEG_INT = /^\d{1,6}$/;
/** The server's bounds (`@vello/fare-quote`): a typo guard on the percent fee; the bands and pairs per rule. */
const MAX_PERCENT_BP = 5000;
export const MAX_TARIFF_BANDS = 32;
export const MAX_TARIFF_PAIRS = 64;

export function emptyBandRow(): TariffBandRow {
  return { fromKm: '', toKm: '', priceMinor: undefined };
}
export function emptyPairRow(): TariffPairRow {
  return { destinationMarketId: '', fixedMinor: undefined, additionalMinor: undefined };
}

const kmToMeters = (km: string): number => Math.round(Number(km) * 1000);
const metersToKm = (meters: number): string => String(meters / 1000);

export function emptyTariffForm(marketId = '', categoryCode = '', currency = ''): TariffForm {
  return {
    marketId, categoryCode, currency, effectiveFromLocal: '', effectiveToLocal: '',
    baseFareMinor: undefined, minimumFareMinor: undefined, distanceRateMinor: undefined, distancePerMeters: '',
    timeRateMinor: undefined, timePerSeconds: '', quoteTtlSeconds: '',
    perStopFeeMinor: undefined, bookingFeeMinor: undefined, percentFeePercent: '',
    waitRateMinor: undefined, freeWaitMinutes: '', waitPenaltyMinor: undefined, maxWaitMinutes: '',
    bands: [], pairs: [], reason: '',
  };
}

/** A new-version form pre-filled from an existing version's rates (the effective window and reason start empty). */
export function tariffFormFromRule(rule: PricingRuleView): TariffForm {
  const big = (s: string): bigint | undefined => (/^\d+$/.test(s) ? BigInt(s) : undefined);
  return {
    ...emptyTariffForm(rule.marketId, rule.categoryCode, rule.currency),
    baseFareMinor: big(rule.baseFareMinor),
    minimumFareMinor: big(rule.minimumFareMinor),
    distanceRateMinor: big(rule.distanceRate.minorUnits),
    distancePerMeters: String(rule.distanceRate.perUnit),
    timeRateMinor: big(rule.timeRate.minorUnits),
    timePerSeconds: String(rule.timeRate.perUnit),
    quoteTtlSeconds: String(rule.quoteTtlSeconds),
    perStopFeeMinor: rule.perStopFeeMinor !== undefined ? big(rule.perStopFeeMinor) : undefined,
    bookingFeeMinor: rule.bookingFeeMinor !== undefined ? big(rule.bookingFeeMinor) : undefined,
    percentFeePercent: rule.percentFeeBasisPoints !== undefined ? String(rule.percentFeeBasisPoints / 100) : '',
    // The wait rate is shown per MINUTE: the stored rate is per `perUnit` seconds, re-expressed per 60 s (exact for a per-minute rate).
    waitRateMinor: rule.waitRate !== undefined && rule.waitRate.perUnit === 60 ? big(rule.waitRate.minorUnits) : undefined,
    freeWaitMinutes: rule.freeWaitSeconds !== undefined ? String(rule.freeWaitSeconds / 60) : '',
    waitPenaltyMinor: rule.waitPenaltyMinor !== undefined ? big(rule.waitPenaltyMinor) : undefined,
    maxWaitMinutes: rule.maxWaitSeconds !== undefined ? String(rule.maxWaitSeconds / 60) : '',
    bands: (rule.distanceBands ?? []).map((b: PricingDistanceBandOnWire) => ({ fromKm: metersToKm(b.fromMeters), toKm: metersToKm(b.toMeters), priceMinor: big(b.priceMinor) })),
    pairs: (rule.intercityPairs ?? []).map((p: PricingIntercityPairOnWire) => ({
      destinationMarketId: p.destinationMarketId,
      fixedMinor: p.fixedMinor !== undefined ? big(p.fixedMinor) : undefined,
      additionalMinor: p.additionalMinor !== undefined ? big(p.additionalMinor) : undefined,
    })),
  };
}

/**
 * Validate the form and build the wire input, or return every issue at once. `timeZone` is the market's IANA zone the
 * effective window was typed in. The server re-validates everything (and assigns the version).
 */
export function normalizeTariffForm(form: TariffForm, timeZone: string | undefined): Result<CreatePricingRuleInput, readonly TariffFormIssue[]> {
  const issues: TariffFormIssue[] = [];
  const need = (field: TariffFormField, present: boolean): boolean => {
    if (!present) issues.push({ field, code: 'REQUIRED' });
    return present;
  };

  const marketId = form.marketId.trim();
  if (need('marketId', marketId !== '') && !MARKET.test(marketId)) issues.push({ field: 'marketId', code: 'INVALID_MARKET' });
  const categoryCode = form.categoryCode.trim();
  if (need('categoryCode', categoryCode !== '') && !CATEGORY.test(categoryCode)) issues.push({ field: 'categoryCode', code: 'INVALID_CATEGORY' });
  const currency = form.currency.trim().toUpperCase();
  if (need('currency', currency !== '') && (!/^[A-Z]{3}$/.test(currency) || !isIso4217Currency(currency))) issues.push({ field: 'currency', code: 'INVALID_CURRENCY' });

  let effectiveFrom: string | undefined;
  if (need('effectiveFromLocal', form.effectiveFromLocal.trim() !== '')) {
    effectiveFrom = timeZone !== undefined ? zonedLocalToUtc(form.effectiveFromLocal, timeZone) : undefined;
    if (effectiveFrom === undefined) issues.push({ field: 'effectiveFromLocal', code: 'INVALID_LOCAL_TIME' });
  }
  let effectiveTo: string | undefined;
  if (form.effectiveToLocal.trim() !== '') {
    effectiveTo = timeZone !== undefined ? zonedLocalToUtc(form.effectiveToLocal, timeZone) : undefined;
    if (effectiveTo === undefined) issues.push({ field: 'effectiveToLocal', code: 'INVALID_LOCAL_TIME' });
    else if (effectiveFrom !== undefined && Date.parse(effectiveTo) <= Date.parse(effectiveFrom)) issues.push({ field: 'effectiveToLocal', code: 'WINDOW_ORDER' });
  }

  need('baseFareMinor', form.baseFareMinor !== undefined);
  need('minimumFareMinor', form.minimumFareMinor !== undefined);
  need('distanceRateMinor', form.distanceRateMinor !== undefined);
  need('timeRateMinor', form.timeRateMinor !== undefined);
  const positive = (field: 'distancePerMeters' | 'timePerSeconds' | 'quoteTtlSeconds'): number | undefined => {
    const raw = form[field].trim();
    if (!need(field, raw !== '')) return undefined;
    if (!POSITIVE_INT.test(raw)) {
      issues.push({ field, code: 'INVALID_POSITIVE_INTEGER' });
      return undefined;
    }
    return Number(raw);
  };
  const distancePer = positive('distancePerMeters');
  const timePer = positive('timePerSeconds');
  const ttl = positive('quoteTtlSeconds');
  const reason = form.reason.trim();
  need('reason', reason !== '');

  // ---- D463 components (all optional; a member that is typed but malformed is an issue, never silently dropped) ----
  const percentRaw = form.percentFeePercent.trim();
  let percentFeeBasisPoints: number | undefined;
  if (percentRaw !== '') {
    const bp = PERCENT.test(percentRaw) ? Math.round(Number(percentRaw) * 100) : NaN;
    if (!(bp >= 0 && bp <= MAX_PERCENT_BP)) issues.push({ field: 'percentFeePercent', code: 'INVALID_PERCENT' });
    else percentFeeBasisPoints = bp;
  }
  const minutes = (field: 'freeWaitMinutes' | 'maxWaitMinutes'): number | undefined => {
    const raw = form[field].trim();
    if (raw === '') return undefined;
    if (!NON_NEG_INT.test(raw) || (field === 'maxWaitMinutes' && Number(raw) < 1)) {
      issues.push({ field, code: 'INVALID_MINUTES' });
      return undefined;
    }
    return Number(raw);
  };
  const freeWait = minutes('freeWaitMinutes');
  const maxWait = minutes('maxWaitMinutes');
  const anyWait = form.waitRateMinor !== undefined || form.waitPenaltyMinor !== undefined || freeWait !== undefined || maxWait !== undefined || form.freeWaitMinutes.trim() !== '' || form.maxWaitMinutes.trim() !== '';
  if (anyWait) {
    // A wait charge needs its rate and its cap (the server refuses a rate with no cap, and a cap, allowance or penalty with no rate).
    if (form.waitRateMinor === undefined) issues.push({ field: 'waitRateMinor', code: 'WAIT_INCOMPLETE' });
    if (maxWait === undefined && form.maxWaitMinutes.trim() === '') issues.push({ field: 'maxWaitMinutes', code: 'WAIT_INCOMPLETE' });
  }
  const bands: PricingDistanceBandOnWire[] = [];
  let bandsBad = form.bands.length > MAX_TARIFF_BANDS;
  let previousTo = -1;
  for (const row of form.bands) {
    const from = row.fromKm.trim();
    const to = row.toKm.trim();
    if (!KM.test(from) || !KM.test(to) || row.priceMinor === undefined) { bandsBad = true; continue; }
    const fromMeters = kmToMeters(from);
    const toMeters = kmToMeters(to);
    if (toMeters <= fromMeters || fromMeters < previousTo) bandsBad = true;
    previousTo = toMeters;
    bands.push({ fromMeters, toMeters, priceMinor: row.priceMinor.toString() });
  }
  if (bandsBad) issues.push({ field: 'bands', code: 'INVALID_BANDS' });
  const pairs: PricingIntercityPairOnWire[] = [];
  let pairsBad = form.pairs.length > MAX_TARIFF_PAIRS;
  const seenPairs = new Set<string>();
  for (const row of form.pairs) {
    const destination = row.destinationMarketId.trim();
    if (!MARKET.test(destination) || destination === marketId || (row.fixedMinor === undefined && row.additionalMinor === undefined) || seenPairs.has(destination)) { pairsBad = true; continue; }
    seenPairs.add(destination);
    pairs.push({
      originMarketId: marketId, destinationMarketId: destination,
      ...(row.fixedMinor !== undefined ? { fixedMinor: row.fixedMinor.toString() } : {}),
      ...(row.additionalMinor !== undefined ? { additionalMinor: row.additionalMinor.toString() } : {}),
    });
  }
  if (pairsBad) issues.push({ field: 'pairs', code: 'INVALID_PAIRS' });

  if (issues.length > 0) return err(issues);
  return ok({
    marketId,
    categoryCode,
    currency,
    effectiveFrom: effectiveFrom as string,
    ...(effectiveTo !== undefined ? { effectiveTo } : {}),
    baseFareMinor: (form.baseFareMinor as bigint).toString(),
    distanceRate: { minorUnits: (form.distanceRateMinor as bigint).toString(), perUnit: distancePer as number },
    timeRate: { minorUnits: (form.timeRateMinor as bigint).toString(), perUnit: timePer as number },
    minimumFareMinor: (form.minimumFareMinor as bigint).toString(),
    quoteTtlSeconds: ttl as number,
    ...(form.perStopFeeMinor !== undefined ? { perStopFeeMinor: form.perStopFeeMinor.toString() } : {}),
    ...(form.bookingFeeMinor !== undefined ? { bookingFeeMinor: form.bookingFeeMinor.toString() } : {}),
    ...(percentFeeBasisPoints !== undefined ? { percentFeeBasisPoints } : {}),
    ...(form.waitRateMinor !== undefined && maxWait !== undefined
      ? {
          waitRate: { minorUnits: form.waitRateMinor.toString(), perUnit: 60 },
          maxWaitSeconds: maxWait * 60,
          ...(freeWait !== undefined ? { freeWaitSeconds: freeWait * 60 } : {}),
          ...(form.waitPenaltyMinor !== undefined ? { waitPenaltyMinor: form.waitPenaltyMinor.toString() } : {}),
        }
      : {}),
    ...(bands.length > 0 ? { distanceBands: bands } : {}),
    ...(pairs.length > 0 ? { intercityPairs: pairs } : {}),
    reason,
  });
}

// ---- history + four-eyes --------------------------------------------------------------------------------

/** The version number the server encodes at the end of `pricingVersion` (`…-v3` → 3); 0 when absent. */
export function pricingVersionNumber(pricingVersion: string): number {
  const m = /-v(\d+)$/.exec(pricingVersion);
  return m === null ? 0 : Number(m[1]);
}

/** Newest version first. */
export function tariffHistory(rules: readonly PricingRuleView[]): readonly PricingRuleView[] {
  return [...rules].sort((a, b) => pricingVersionNumber(b.pricingVersion) - pricingVersionNumber(a.pricingVersion));
}

export type TariffTone = 'neutral' | 'success' | 'warning' | 'danger';

export function tariffStatusTone(status: PricingRuleStatus): TariffTone {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'PENDING_APPROVAL':
      return 'warning';
    case 'DRAFT':
      return 'neutral';
    case 'ARCHIVED':
      return 'neutral';
  }
}

/** The ACTIVE version whose effective window contains `now` (the one that prices quotes), if any. */
export function effectiveTariff(rules: readonly PricingRuleView[], now: string): PricingRuleView | undefined {
  const t = Date.parse(now);
  return tariffHistory(rules).find((r) => r.status === 'ACTIVE' && Date.parse(r.effectiveFrom) <= t && (r.effectiveTo === undefined || t < Date.parse(r.effectiveTo)));
}

export interface TariffApprovalView {
  /** Awaiting a second operator. */
  readonly pending: boolean;
  /** The viewer authored this version: the server will refuse their approval (four-eyes). */
  readonly viewerIsMaker: boolean;
}

/**
 * What the screen says about approving a version. It never hides the action from the maker — the server is the gate and
 * its refusal is shown — but it tells the maker up front that a second operator must approve.
 */
export function tariffApprovalView(rule: PricingRuleView, viewerId: string | undefined, selfApproval = false): TariffApprovalView {
  // D868: a GLOBAL super administrator approves their own version - the "a second operator must approve" note is not theirs.
  return { pending: rule.status === 'PENDING_APPROVAL', viewerIsMaker: !selfApproval && viewerId !== undefined && viewerId === rule.maker };
}
